import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { tr } from "../lib/language";

export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ number, children, dark = false }) => (
  <p
    className={`flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] ${
      dark ? "text-white/70" : "text-dwc-green"
    }`}
  >
    {number && <span className="text-dwc-crimson">{number}</span>}
    <span className={`h-px w-8 ${dark ? "bg-white/40" : "bg-dwc-green/40"}`} aria-hidden="true" />
    {children}
  </p>
);

export const SectionHeading = ({ eyebrow, number, title, text, dark = false, className = "" }) => (
  <div className={`max-w-3xl ${className}`}>
    {eyebrow && (
      <Reveal>
        <Eyebrow number={number} dark={dark}>{tr(eyebrow)}</Eyebrow>
      </Reveal>
    )}
    <Reveal delay={0.08}>
      <h2 className={`mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-balance leading-[1.08] ${dark ? "text-white" : "text-dwc-charcoal"}`}>
        {tr(title)}
      </h2>
    </Reveal>
    {text && (
      <Reveal delay={0.16}>
        <p className={`mt-5 text-base md:text-lg leading-relaxed ${dark ? "text-white/75" : "text-dwc-charcoal/70"}`}>{tr(text)}</p>
      </Reveal>
    )}
  </div>
);

export const PrimaryButton = ({ children, onClick, href, testId, className = "", type = "button" }) => {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-xl bg-dwc-green px-7 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-[0_10px_30px_rgba(23,107,58,0.28)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-dwc-green-dark hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(23,107,58,0.34)] ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
    </>
  );
  if (href) return <a href={href} data-testid={testId} className={cls} onClick={onClick}>{inner}</a>;
  return <button type={type} data-testid={testId} onClick={onClick} className={cls}>{inner}</button>;
};

export const SecondaryButton = ({ children, onClick, href, testId, className = "", dark = false }) => {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-xl border-2 px-7 py-4 text-sm font-bold uppercase tracking-wide transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 ${
    dark
      ? "border-white/40 bg-transparent text-white hover:border-white hover:bg-white/10"
      : "border-dwc-green bg-white text-dwc-green hover:bg-dwc-soft"
  } ${className}`;
  const inner = (
    <>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
    </>
  );
  if (href) return <a href={href} data-testid={testId} className={cls} onClick={onClick}>{inner}</a>;
  return <button type="button" data-testid={testId} onClick={onClick} className={cls}>{inner}</button>;
};

export const Marquee = ({ items, dark = true, className = "" }) => {
  const row = items.map((item, i) => (
    <span key={i} className="mx-8 inline-flex items-center gap-8 whitespace-nowrap">
      <span className={`text-sm font-extrabold uppercase tracking-[0.3em] ${dark ? "text-white/85" : "text-dwc-green/80"}`}>{item}</span>
      <span className="h-1.5 w-1.5 rounded-full bg-dwc-crimson" aria-hidden="true" />
    </span>
  ));
  return (
    <div className={`relative overflow-hidden py-5 ${dark ? "bg-dwc-green-deep" : "bg-dwc-soft"} ${className}`} aria-hidden="true">
      <div className="flex w-max animate-marquee">
        <div className="flex">{row}</div>
        <div className="flex">{row}</div>
      </div>
    </div>
  );
};

export const CountUp = ({ value, suffix = "", testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v).toLocaleString("en-IN")),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} data-testid={testId}>
      {display}
      {suffix}
    </span>
  );
};

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-1">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: delay + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);
